// Podaci "Kuhinjski ekran v2": izvještaj svakih 10 s, kartice, promjena stanja sa Poništi, nove narudžbe.
// Plan: "Dizajn kuhinjskog ekrana.html"; logika bez Vue-a: @/utils/kdsTickets.
//
// Promjena stanja ide odmah na server (change-order-status, isto što i stari ekran), a kartica se pomjera odmah
// (overrides) dok server ne potvrdi. Odbijanje i otkazivanje ostaju 8 s u redu prije slanja, pa Poništi stvarno
// ništa ne šalje. Poništi za ostala stanja šalje prethodno stanje nazad.
//
// Očekuje u komponenti: settings, type, q, onlyLate, rest, chooserId, menu, confirmId, sheet, detId, pop,
// live, focusKey(), focusCard(), chime(), nowTs/viewNow/lastOk/startedAt/browserOnline iz kds2Device.
import reportService from "@/services/report";
import {
  TH,
  actionOf,
  countLate,
  decorate,
  emptyMemo,
  filterTickets,
  findNew,
  groupByStage,
  MEMO_KEY,
  NOTE_FIELD,
  norm,
  normalizeReport,
  pruneOverrides,
  reconcileMemo,
  sanitizeMemo,
  toISODate,
} from "@/utils/kdsTickets";

const DAY_MS = 24 * 3600 * 1000;

function readMemo() {
  try {
    return sanitizeMemo(JSON.parse(localStorage.getItem(MEMO_KEY)));
  } catch (e) {
    return emptyMemo();
  }
}

export default {
  data() {
    return {
      reportData: {},
      loaded: false,
      loading: false,
      failed: false,
      lastOk: 0,
      loadedAt: Date.now(),
      startedAt: Date.now(),
      memo: readMemo(),
      overrides: {},
      newIds: {},
      undo: null,
      errMsg: "",
      dateMode: "live",
      customDate: toISODate(new Date()),
    };
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    isAdmin() {
      return !!this.user && this.user.type === "admin";
    },
    adminRestaurants() {
      if (!this.isAdmin || !this.user.restaurants) return [];
      return this.user.restaurants.filter((item) => item.settings && item.settings.lice_bez_ugovora);
    },
    adminRestaurantIds() {
      return this.adminRestaurants.map((item) => item.id).join(",");
    },
    dateKey() {
      return this.dateMode === "live" ? "live" : this.customDate;
    },
    offline() {
      return !this.browserOnline || this.failed;
    },

    // kartice sa servera (stanja kakva jesu) i kartice kakve ih vidi kuhinja (sa našim nepotvrđenim promjenama)
    baseTickets() {
      return normalizeReport(this.reportData, {
        latin: (text) => this.toLatin(text),
        now: this.loadedAt,
        cutoff: this.dateMode === "live" ? this.loadedAt - DAY_MS : null,
        noteField: NOTE_FIELD,
      });
    },
    tickets() {
      return decorate(this.baseTickets, this.memo, this.overrides);
    },
    qn() {
      return norm(this.q.trim());
    },
    // po restoranu i pretrazi (brojevi na dugmadima vrste)
    scoped() {
      return filterTickets(this.tickets, { rest: this.rest, q: this.qn }, this.viewNow);
    },
    shown() {
      return filterTickets(this.scoped, { type: this.type, onlyLate: this.onlyLate }, this.viewNow);
    },
    groups() {
      return groupByStage(this.shown, this.viewNow);
    },
    typeCounts() {
      const counts = { all: this.scoped.length, 0: 0, 1: 0, 2: 0 };
      this.scoped.forEach((t) => {
        counts[t.type] += 1;
      });
      return counts;
    },
    // koliko ih kasni u izabranom restoranu (ne zavisi od pretrage i vrste)
    lateTotal() {
      return countLate(filterTickets(this.tickets, { rest: this.rest }, this.viewNow), this.viewNow);
    },
    restOptions() {
      return this.adminRestaurants.map((item) => ({
        id: item.id,
        name: this.toLatin(item.name || ""),
        count: this.tickets.filter((t) => t.restaurant && String(t.restaurant.id) === String(item.id)).length,
      }));
    },
  },
  created() {
    this._known = null; // ID-evi iz prošlog odgovora (null = još nema; prvi odgovor ne pravi zvuk)
    this._inFlight = false;
    this._again = false;
    this._pollTimer = null;
    this._undoTimer = null;
    this._errTimer = null;
    this._deferred = {};
    this._chain = {};
  },
  mounted() {
    this.loadTickets();
    this._pollTimer = setInterval(this.loadTickets, TH.pollMs);
  },
  beforeDestroy() {
    clearInterval(this._pollTimer);
    clearTimeout(this._undoTimer);
    clearTimeout(this._errTimer);
    // odbijanja koja još čekaju u redu ne smiju da se izgube pri izlasku sa ekrana
    Object.keys(this._deferred).forEach((id) => {
      clearTimeout(this._deferred[id]);
      this.commit(Number(id));
    });
  },
  watch: {
    dateKey() {
      this._known = null;
      this.reportData = {};
      this.loaded = false;
      this.loadTickets();
    },
  },
  methods: {
    byId(id) {
      return this.tickets.find((t) => t.id === id) || null;
    },
    announce(text) {
      this.live = text;
    },
    flashErr(text, ms = 3200) {
      this.errMsg = text;
      clearTimeout(this._errTimer);
      this._errTimer = setTimeout(() => {
        this.errMsg = "";
      }, ms);
    },
    // šta je server odgovorio na promjenu stanja, da se vidi koje stanje ne prima: " (rejected: Nepoznat status narudžbe)"
    sendWhy(error, status) {
      const res = error && error.response;
      const data = res && res.data;
      const text = data && (typeof data.message === "string" ? data.message : typeof data.error === "string" ? data.error : "");
      return " (" + status + (text ? ": " + text.slice(0, 80) : res ? ": HTTP " + res.status : "") + ")";
    },
    saveMemo() {
      try {
        localStorage.setItem(MEMO_KEY, JSON.stringify(this.memo));
      } catch (e) {
        // bez pamćenja, ekran radi dalje
      }
    },

    // Period za server računa se pri SVAKOM pitanju (ne smije biti computed: kuhinjski ekran radi danima, pa bi poslije
    // ponoći ostao jučerašnji datum): uživo = od jučer do danas, inače jedan izabrani dan.
    rangeNow() {
      if (this.dateMode === "live") {
        const now = Date.now();
        return { date: toISODate(new Date(now - DAY_MS)), date2: toISODate(new Date(now)) };
      }
      return { date: this.customDate, date2: this.customDate };
    },

    // ---------- izvještaj ----------
    loadTickets() {
      if (this.isAdmin && !this.adminRestaurantIds) return Promise.resolve();
      if (this._inFlight) {
        this._again = true;
        return Promise.resolve();
      }
      this._inFlight = true;
      this.loading = true;
      const key = this.dateKey;
      const range = this.rangeNow();
      const config = { timeout: 8000 };
      const request = this.isAdmin
        ? reportService.getCompanyMultipleNoContract({ ...range, restaurants: this.adminRestaurantIds }, config)
        : reportService.getNoContractDelivery(this.$store.state.restaurant && this.$store.state.restaurant.id, range, config);
      return request
        .then((response) => {
          if (key !== this.dateKey) return; // dan je u međuvremenu promijenjen
          this.loadedAt = Date.now();
          this.reportData = (response && response.data) || {};
          this.lastOk = this.loadedAt;
          this.failed = false;
          this.loaded = true;
          this.afterLoad();
        })
        .catch((error) => {
          console.error("Error loading kitchen display data:", error);
          this.failed = true;
        })
        .finally(() => {
          this._inFlight = false;
          this.loading = false;
          if (this._again) {
            this._again = false;
            this.loadTickets();
          }
        });
    },
    // poslije svakog odgovora: nove narudžbe (zvuk + NOVO), vrijeme stanja, potvrđene promjene
    afterLoad() {
      const base = this.baseTickets;
      const now = Date.now();
      if (this.dateMode === "live") {
        const fresh = findNew(this._known, base);
        if (fresh.length) {
          fresh.forEach((t) => this.$set(this.newIds, t.id, true));
          this.chime();
          this._lastRemind = now;
          this.announce(this.tx("live.new", { id: fresh[0].id }));
        }
        this._known = new Set(base.map((t) => t.id));
      } else {
        this._known = null;
      }
      const present = new Set(base.map((t) => t.id));
      Object.keys(this.newIds).forEach((id) => {
        if (!present.has(Number(id))) this.$delete(this.newIds, id);
      });
      const hints = {};
      Object.keys(this.overrides).forEach((id) => {
        const ov = this.overrides[id];
        hints[id] = { phase: ov.state === "in-progress" ? "accepted" : ov.state, at: ov.since };
      });
      const memo = reconcileMemo(this.memo, base, now, hints);
      if (memo !== this.memo) {
        this.memo = memo;
        this.saveMemo();
      }
      const serverStates = {};
      base.forEach((t) => {
        serverStates[t.id] = t.state;
      });
      const next = pruneOverrides(this.overrides, serverStates, now);
      if (next !== this.overrides) this.overrides = next;
    },
    clearNew(id) {
      if (this.newIds[id]) this.$delete(this.newIds, id);
    },

    // ---------- promjena stanja ----------
    didText(to, from, est) {
      if (to === "accepted") return est ? this.tx("did.acceptedEst", { est: this.dur(est) }) : this.tx("did.accepted");
      if (to === "ready") return this.tx(from === "accepted" || from === "in-progress" ? "did.ready" : "did.readyNow");
      return this.tx("did." + (to === "on-hold" ? "onHold" : to));
    },
    // opts.est: procjena u minutama pri prihvatanju
    move(id, to, opts = {}) {
      const t = this.byId(id);
      if (!t || t.state === to) return;
      if (this.offline) {
        this.flashErr(this.tx("err.offline"));
        return;
      }
      const now = Date.now();
      const est = to === "accepted" && opts.est ? opts.est : null;
      const deferred = to === "rejected" || to === "cancelled";
      const prev = { state: t.state, since: t.since, acc: t.acc, est: t.est };
      this.$set(this.overrides, id, {
        state: to,
        since: now,
        acc: to === "accepted" ? now : null,
        est,
        prev,
        phase: deferred ? "queued" : "sending",
        until: now + 20000,
      });
      if (est) {
        this.memo = { ...this.memo, est: { ...this.memo.est, [id]: { m: est, at: now } } };
        this.saveMemo();
        this.settings.lastEst = est;
      }
      this.clearNew(id);
      this.chooserId = null;
      this.menu = null;
      this.confirmId = null;
      this.undo = { id, to, prev, deferred, at: now, label: this.didText(to, t.state, est) };
      this.announce(this.tx("toast.order", { id, what: this.undo.label }));
      clearTimeout(this._undoTimer);
      this._undoTimer = setTimeout(() => {
        if (this.undo && this.undo.at === now) this.undo = null;
      }, TH.undoMs);
      if (deferred) {
        clearTimeout(this._deferred[id]);
        this._deferred[id] = setTimeout(() => {
          delete this._deferred[id];
          this.commit(id);
        }, TH.undoMs);
      } else {
        this.commit(id);
      }
      this.focusCard(id);
    },
    // šalje stanje iz naše promjene; zahtjevi za istu narudžbu idu jedan za drugim
    commit(id) {
      const ov = this.overrides[id];
      const t = this.baseTickets.find((x) => x.id === id);
      if (!ov) return;
      if (!t) {
        this.$delete(this.overrides, id);
        return;
      }
      ov.phase = "sending";
      // "status" je polje koje backend čita, "state" se šalje uz njega kao i do sada
      const payload = { ...t.raw, order_id: t.raw.id, user_id: t.rawGuest.id, status: ov.state, state: ov.state };
      this._chain[id] = (this._chain[id] || Promise.resolve())
        .then(() => reportService.changeOrderStatus(payload))
        .then(() => {
          if (this.overrides[id] === ov) {
            ov.phase = "sent";
            ov.until = Date.now() + 15000;
          }
          this.loadTickets();
        })
        .catch((error) => {
          console.error("Error changing order status:", ov.state, error);
          if (this.overrides[id] === ov) this.$delete(this.overrides, id);
          if (this.undo && this.undo.id === id) this.undo = null;
          this.flashErr(this.tx("err.send") + this.sendWhy(error, ov.state), 7000);
          this.loadTickets();
        });
    },
    undoLast() {
      const u = this.undo;
      if (!u) return;
      this.undo = null;
      clearTimeout(this._undoTimer);
      const ov = this.overrides[u.id];
      this.announce(this.tx("live.undone"));
      if (u.deferred && ov && ov.phase === "queued") {
        clearTimeout(this._deferred[u.id]);
        delete this._deferred[u.id];
        this.$delete(this.overrides, u.id);
        this.focusCard(u.id);
        return;
      }
      if (this.offline) {
        this.flashErr(this.tx("err.offline"));
        return;
      }
      const p = u.prev;
      this.$set(this.overrides, u.id, {
        state: p.state,
        since: p.since,
        acc: p.acc,
        est: p.est,
        prev: null,
        phase: "sending",
        until: Date.now() + 20000,
      });
      this.commit(u.id);
      this.focusCard(u.id);
    },

    // ---------- glavno dugme kartice ----------
    go(id) {
      const t = this.byId(id);
      const a = t && actionOf(t);
      if (!a) return;
      if (a.to === "accepted") {
        if (this.settings.quick) {
          this.move(id, "accepted", { est: this.settings.lastEst });
          return;
        }
        this.clearNew(id);
        this.menu = null;
        this.chooserId = id;
        this.focusKey("pick-" + this.settings.lastEst);
        return;
      }
      this.move(id, a.to);
    },
    pick(id, minutes) {
      if (minutes === 0) this.move(id, "ready");
      else this.move(id, "accepted", { est: minutes });
    },
  },
};
